import { useEffect, useRef, useState } from "react";
import { Activity, Archive, Bot, BookOpen, Database, Library, LogOut, Search, UserCog } from "lucide-react";
import { AgentPage } from "./agent/agent-page";
import { UserManagementPage } from "./admin/user-management-page";
import { BackupPage } from "./backup/backup-page";
import { Button } from "@web/components/ui/button";
import { LoginPage } from "./auth/login-page";
import { MonitorPage } from "./monitor/monitor-page";
import { ThemeToggleButton } from "@web/components/features/theme-toggle-button";
import { useReaderWorkspace } from "./reader/reader-workspace";
import { appRoutes, navigateTo, readRoute, type AppRoute } from "./routes";
import { ReaderPage } from "./reader/reader-page";
import { SearchPage } from "./search/search-page";
import { ShelfPage } from "./shelf/shelf-page";
import { SourcesPage } from "./sources/sources-page";
import "./index.css";

export function App() {
  const workspace = useReaderWorkspace();
  const [route, setRoute] = useState<AppRoute>(() => readRoute(window.location.pathname));
  const previousRouteIdRef = useRef(route.id);
  const shouldNormalizeShelfMessageRef = useRef(false);

  useEffect(() => {
    // 根路径仍然回到书架  搜索现在有独立路由
    if (window.location.pathname === "/") navigateTo("/shelf");

    const syncRoute = () => setRoute(readRoute(window.location.pathname));
    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  }, []);

  useEffect(() => {
    if (route.id !== "reader") document.title = "Reader";
  }, [route.id]);

  useEffect(() => {
    // Radix 的下拉层挂在 body 下 主题必须同步到 html 才能覆盖 portal
    document.documentElement.classList.toggle("dark", workspace.preference.theme === "dark");
  }, [workspace.preference.theme]);

  useEffect(() => {
    const previousRouteId = previousRouteIdRef.current;
    previousRouteIdRef.current = route.id;

    if (previousRouteId === "reader" && route.id === "shelf") {
      shouldNormalizeShelfMessageRef.current = true;
    } else if (route.id !== "shelf") {
      shouldNormalizeShelfMessageRef.current = false;
    }

    if (shouldNormalizeShelfMessageRef.current && route.id === "shelf" && shouldResetReaderMessage(workspace.message)) {
      workspace.handleReaderReturned();
    }
  }, [route.id, workspace.message, workspace.handleReaderReturned]);

  const routeIcons = {
    shelf: Library,
    search: Search,
    reader: BookOpen,
    sources: Database,
    users: UserCog,
    monitor: Activity,
    backup: Archive,
    agent: Bot,
  };
  const shellRoutes = appRoutes.filter(item => item.id !== "reader");

  if (!workspace.authReady) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-background text-sm text-muted-foreground">
        正在检查登录状态
      </main>
    );
  }

  if (!workspace.user) {
    return <LoginPage workspace={workspace} />;
  }

  if (route.id === "reader") {
    return (
      <main className={`${workspace.preference.theme === "dark" ? "dark " : ""}min-h-dvh bg-background text-foreground`}>
        <ReaderPage workspace={workspace} />
      </main>
    );
  }

  return (
    <main className={`${workspace.preference.theme === "dark" ? "dark " : ""}min-h-dvh bg-background text-foreground`}>
      <header className="shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-4 px-4">
          <button type="button" onClick={() => navigateTo("/shelf")} className="flex items-center gap-2 font-semibold">
            <Library className="size-5" />
            <span>Reader</span>
          </button>
          <nav className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
            {shellRoutes.map(item => {
              const Icon = routeIcons[item.id];

              return (
                <Button
                  key={item.id}
                  type="button"
                  aria-label={item.label}
                  variant={route.id === item.id ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => navigateTo(item.path)}
                >
                  <Icon className="size-4" />
                  <span className="hidden sm:inline">{item.label}</span>
                </Button>
              );
            })}
          </nav>
          <ThemeToggleButton theme={workspace.preference.theme} onToggle={workspace.handleToggleTheme} />
          <Button type="button" variant="ghost" size="sm" onClick={workspace.handleLogout} aria-label="退出登录">
            <LogOut className="size-4" />
            <span className="hidden sm:inline">退出</span>
          </Button>
        </div>
      </header>

      <section className="mx-auto w-full max-w-7xl px-4 py-4 sm:py-6">
        <section className="min-w-0 space-y-4">
          {route.id === "shelf" && <ShelfPage workspace={workspace} />}
          {route.id === "search" && <SearchPage workspace={workspace} />}
          {route.id === "sources" && <SourcesPage workspace={workspace} />}
          {route.id === "users" && <UserManagementPage workspace={workspace} />}
          {route.id === "monitor" && <MonitorPage workspace={workspace} />}
          {route.id === "backup" && <BackupPage workspace={workspace} />}
          {route.id === "agent" && <AgentPage />}
        </section>
      </section>
    </main>
  );
}

export default App;

function shouldResetReaderMessage(message: string): boolean {
  return message.startsWith("已预取") || message.startsWith("已追加") || message.startsWith("正在加载下一章");
}
